import React, { useEffect, useState, useMemo } from "react";
import { MentionEditor } from "./Editor";
import { AccessibleThreadCard } from "./AccessibleThreadCard";
import { MobileThreadCard } from "./MobileThreadCard";
import { ThreadFilters } from "./ThreadFilters";
import { ChevronDown, PenLine } from "lucide-react";
import profilePicture from "../../../assets/icons/perfilionvo.png";
import { useDraftSave } from "../hooks/useDraftSave";
import { useThreadActions } from "../hooks/useThreadActions";
import { LoadingOverlay } from "./ui/LoadingSpinner";
import {
  editComment,
  editThreadForm,
  handleDeleteComment,
  handleDeleteThread,
  handleSubmitComment,
  handleSubmitLikes,
  handleSubmitThread,
  putAnchor,
  putSeen,
} from "../../../utils/ApiCall";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";

export function ThreadList({
  threads,
  setThreads,
  backendUrl,
  currentUserId,
  currentUserEmail,
  currentUserUsername,
  pathname,
  threadKey,
  row,
  avatar,
  initiatorValue,
  setNumberOfThreads,
}) {
  const [showEditor, setShowEditor] = React.useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [filteredThreads, setFilteredThreads] = useState(threads);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Context and hooks
  const {
    draft,
    updateDraft,
    clearDraft,
    hasDraft,
    isAutoSaving,
    formatLastSaved
  } = useDraftSave('new_thread');

  const {
    handleThreadDelete,
    handleCommentDelete,
    handleThreadEdit,
    handleCommentEdit
  } = useThreadActions({
    pathname,
    row,
    setThreads,
    setNumberOfThreads
  });

  const location = useLocation();
  const board = location?.pathname?.slice(1);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update filtered threads when threads change
  useEffect(() => {
    setFilteredThreads(threads);
  }, [threads]);

  // Handle thread filtering
  const handleFilterChange = (filters) => {
    let filtered = [...threads];

    // Search filter
    if (filters.searchTerm) {
      const searchTerm = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(thread =>
        thread.content?.toLowerCase().includes(searchTerm) ||
        thread.user?.username?.toLowerCase().includes(searchTerm) ||
        thread.comments?.some(comment =>
          comment.content?.toLowerCase().includes(searchTerm) ||
          comment.user?.username?.toLowerCase().includes(searchTerm)
        )
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      switch (filters.status) {
        case 'unread':
          filtered = filtered.filter(thread =>
            !thread.seen_by?.some(user => user.id === currentUserId)
          );
          break;
        case 'read':
          filtered = filtered.filter(thread =>
            thread.seen_by?.some(user => user.id === currentUserId)
          );
          break;
        case 'anchored':
          filtered = filtered.filter(thread => thread.anchored);
          break;
        default:
          break;
      }
    }

    // Author filter
    if (filters.author) {
      filtered = filtered.filter(thread => thread.user?.id === filters.author);
    }

    // Date range filter
    if (filters.dateRange) {
      const now = new Date();
      let startDate;
      
      switch (filters.dateRange) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered.filter(thread =>
          new Date(thread.createdAt) >= startDate
        );
      }
    }

    // Attachments filter
    if (filters.hasAttachments) {
      filtered = filtered.filter(thread =>
        thread.media?.length > 0 ||
        thread.comments?.some(comment => comment.media?.length > 0)
      );
    }

    setFilteredThreads(filtered);
  };
  const handleFormSubmitThread = async (
    content,
    files,
    selectedUser,
    selectedGroup
  ) => {
    setIsUploading(true);
    const normalizedUsers = Array.isArray(selectedUser)
      ? selectedUser.flat()
      : [];
    try {
      const response = await handleSubmitThread(
        pathname,
        threadKey,
        files,
        row,
        "",
        content,
        normalizedUsers,
        selectedGroup
      );

      if (response && response.data) {
        let rowThread = response.data;
        let thread = rowThread.threads;

        setThreads(thread);
        setNumberOfThreads(thread.length);
        setIsUploading(false);
        
        clearDraft();
      }
    } catch (error) {
      console.error("Error al enviar el thread:", error);
    } finally {
      setShowEditor(false);
    }
  };

  const handleFormSubmitLike = async (thread, comment) => {
    const response = await handleSubmitLikes(pathname, thread, comment, row);
    setThreads(response);
  };

  const handleFormSubmitComment = async (
    id,
    content,
    files,
    selectedUsers,
    selectedGroup,
    parentId = null
  ) => {
    const response = await handleSubmitComment(
      pathname,
      id,
      threadKey,
      files,
      row,
      content,
      selectedUsers,
      selectedGroup,
      parentId
    );

    if (response && response.data && response.data.threads) {
      const { threads } = response.data;
      setThreads(threads);
    }
  };

  const handleEditThread = async (threadId, content, files, removedFiles) => {
    try {
      await handleThreadEdit(threadId, content, files, removedFiles, threadKey, board);
    } catch (error) {
      console.error("Error al actualizar el thread:", error);
    }
  };

  const handleEditComment = async (
    threadId,
    commentId,
    content,
    files,
    removedFiles
  ) => {
    try {
      await handleCommentEdit(threadId, commentId, content, files, removedFiles, threadKey, board);
    } catch (error) {
      console.error("Error al actualizar el comentario:", error);
    }
  };

  const handleToggleAnchor = async (thread) => {
    const response = await putAnchor(
      thread.anchored,
      thread.id,
      pathname,
      row.id
    );

    if (response && response.data && response.data.threads) {
      const { threads } = response.data;
      setThreads(threads);
    }
  };

  const handleSeen = async (message, messageType) => {
    let usersMails = await message.seen_by?.map((e) => e.email);
    if (!usersMails.includes(localStorage.email)) {
      const response = await putSeen(message.id, pathname, messageType, row.id);
      if (response && response.data && response.data.threads) {
        const { threads } = response.data;
        setThreads(threads);
      }
    }
  };

  const handleFormDeleteThread = handleThreadDelete;

  const handleFormDeleteComment = handleCommentDelete;

  useEffect(() => {
    if (initiatorValue) {
      const element = document.getElementById(initiatorValue);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [initiatorValue, threads]);

  return (
    <div className="max-w-2xl mx-auto py-8">
      {/* Filters */}
      {/*
      <ThreadFilters
        onFilterChange={handleFilterChange}
        totalThreads={threads.length}
        users={[]} // You might want to pass actual users data
        tags={[]} // You might want to pass actual tags data
      />
     */}
      

      {/* New Thread Button */}
      <button
        onClick={() => setShowEditor(!showEditor)}
        className={`w-full mb-6 flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
          showEditor
            ? "bg-blue-50 border-blue-500 text-blue-700"
            : "bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
            <img
              src={avatar ? `${backendUrl}${avatar}` : profilePicture}
              alt="foto"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex items-center gap-2">
            <PenLine
              size={18}
              className={showEditor ? "text-blue-500" : "text-gray-400"}
            />
            <span className="font-semibold text-lg">
              Empezar nueva conversación
            </span>
            {hasDraft() && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                Borrador guardado
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAutoSaving && (
            <span className="text-xs text-gray-500">Guardando...</span>
          )}
          <ChevronDown
            size={20}
            className={`text-gray-400 transition-transform duration-200 ease-in-out ${
              showEditor ? "rotate-180 text-blue-500" : ""
            }`}
          />
        </div>
      </button>

      {showEditor && (
        <LoadingOverlay loading={isUploading} text="Subiendo...">
          <div className="mb-6 rounded-lg">
            <MentionEditor
              onPublish={handleFormSubmitThread}
              backendUrl={backendUrl}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              isUploading={isUploading}
              initialContent={draft}
         //     onChange={updateDraft}
            />
            {formatLastSaved() && (
              <div className="text-xs text-gray-500 mt-2 px-2">
                {formatLastSaved()}
              </div>
            )}
          </div>
        </LoadingOverlay>
      )}

      {/* Threads List */}
      {filteredThreads.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <div className="text-lg font-medium mb-2">No se encontraron threads</div>
          <div className="text-sm">Prueba ajustando los filtros o crea una nueva conversación</div>
        </div>
      ) : (
        <div className="space-y-4">

          {filteredThreads.map((thread, index) => {
            const ThreadComponent = isMobile ? MobileThreadCard : AccessibleThreadCard;
            
            return (
              <ThreadComponent
                key={thread.id}
                thread={thread}
                index={index}
                backendUrl={backendUrl}
                comments={thread.comments}
                currentUserId={currentUserId}
                currentUserEmail={currentUserEmail}
                currentUserUsername={currentUserUsername}
                profilePicture={profilePicture}
                onComment={handleFormSubmitComment}
                onEditThread={handleEditThread}
                onEditComment={handleEditComment}
                onToggleAnchor={handleToggleAnchor}
                onLike={handleFormSubmitLike}
                onCommentLike={handleFormSubmitLike}
                avatar={avatar}
                handleSeen={handleSeen}
                onDeleteThread={handleFormDeleteThread}
                onDeleteComment={handleFormDeleteComment}
                setIsEditing={setIsEditing}
                isEditing={isEditing}
                // Mobile specific props
                onMarkAsRead={isMobile ? (threadId) => {
                  // Handle mark as read for mobile
                  console.log('Mark as read:', threadId);
                } : undefined}
                onArchive={isMobile ? (threadId) => {
                  // Handle archive for mobile
                  console.log('Archive:', threadId);
                } : undefined}
                onFavorite={isMobile ? (threadId) => {
                  // Handle favorite for mobile
                  console.log('Favorite:', threadId);
                } : undefined}
                onQuickLike={isMobile ? (threadId) => {
                  // Handle quick like for mobile
                  handleFormSubmitLike(thread, null);
                } : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
